import { createApi } from '@reduxjs/toolkit/query/react';

import { AUTH_API_URL } from 'api/APIBase';
import { handleRTKQueryError } from 'services/Error';
import { getBaseQuery } from 'store/api/apiBaseQuery';

const TagType = {
    USER_PROFILE: 'USER_PROFILE',
    LIGHTHOUSE_USER_NAME_MAP: 'LIGHTHOUSE_USER_NAME_MAP',
} as const;

export const authApi = createApi({
    baseQuery: getBaseQuery(AUTH_API_URL),
    reducerPath: 'authApi',
    tagTypes: Object.values(TagType),
    endpoints: (build) => ({
        getUserProfile: build.query<
            {
                fullName: string;
                email: string;
                role: string;
            },
            void
        >({
            query: () => 'profile/lighthouse',
            providesTags: [TagType.USER_PROFILE],
            transformResponse: (response: {
                upn: string;
                full_name: string;
                display_name: string;
            }) => ({
                fullName: response?.full_name,
                email: response?.upn,
                role: response?.display_name,
            }),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving user profile.',
            ),
        }),
        putUserProfile: build.mutation<void, { fullName: string }>({
            query: ({ fullName }) => ({
                url: 'profile/lighthouse',
                method: 'PUT',
                body: { full_name: fullName },
            }),
            transformErrorResponse: handleRTKQueryError('An error occurred updating user profile.'),
            invalidatesTags: (_, error) =>
                !error ? [TagType.USER_PROFILE, TagType.LIGHTHOUSE_USER_NAME_MAP] : [],
        }),
        getLighthouseUserNameMap: build.query<Record<string, string>, void>({
            query: () => 'profile/lighthouse/name_map',
            providesTags: [TagType.LIGHTHOUSE_USER_NAME_MAP],
            transformErrorResponse: handleRTKQueryError('An error occurred retrieving user names.'),
        }),
    }),
});

export const {
    useGetUserProfileQuery,
    usePutUserProfileMutation,
    useGetLighthouseUserNameMapQuery,
} = authApi;
